import { useCallback, useRef } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative } from '../config/apiConfig';

const ALERTS_KEY       = 'finanzapp_alerts_v1';
const LAST_SIGNALS_KEY = 'finanzapp_last_signals_v1';

function loadAlerts() {
  try { return JSON.parse(localStorage.getItem(ALERTS_KEY) || '{}'); } catch { return {}; }
}
function saveAlerts(data) {
  try { localStorage.setItem(ALERTS_KEY, JSON.stringify(data)); } catch { /* storage full */ }
}
function loadLastSignals() {
  try { return JSON.parse(localStorage.getItem(LAST_SIGNALS_KEY) || '{}'); } catch { return {}; }
}
function saveLastSignals(data) {
  try { localStorage.setItem(LAST_SIGNALS_KEY, JSON.stringify(data)); } catch { /* storage full */ }
}

// ── Permission ────────────────────────────────────────────────────────────────

async function requestPermission() {
  if (isNative()) {
    const { display } = await LocalNotifications.requestPermissions();
    return display === 'granted';
  }
  // Browser-Fallback
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}

export async function getPermissionStatus() {
  if (isNative()) {
    const { display } = await LocalNotifications.checkPermissions();
    return display; // 'granted' | 'denied' | 'prompt'
  }
  if (!('Notification' in window)) return 'not-supported';
  return Notification.permission === 'default' ? 'prompt' : Notification.permission;
}

// ── Notification senden ───────────────────────────────────────────────────────

let notifIdCounter = Date.now();

function buildNotificationContent(asset, signalType) {
  const { ticker, name, price, confidence, risk, change: c24, change7d, reasons } = asset;
  const priceStr = `€${Number(price).toFixed(2)}`;

  if (signalType === 'Kaufen') {
    const urgency = confidence >= 80 ? 'High Alert' : confidence >= 65 ? 'Signal' : 'Watchlist';
    const topReason = reasons?.[0] || 'Technische Indikatoren positiv';
    return {
      title: `📈 Watchlist: ${ticker} – Kaufsignal (${urgency})`,
      body: `${name} bei ${priceStr}\n${topReason}`,
      subtitle: `Confidence ${confidence}% · Risiko ${risk}`,
    };
  }

  if (signalType === 'Verkaufen') {
    const isUrgent = Math.abs(c24) >= 5 || Math.abs(change7d) >= 10;
    const label = isUrgent ? 'Kurskorrektur wahrscheinlich' : 'Gewinnmitnahme prüfen';
    const topReason = reasons?.[0] || 'Technische Überhitzung';
    return {
      title: `📉 Watchlist: ${ticker} – heißgelaufen`,
      body: `${name} bei ${priceStr}\n${label}: ${topReason}`,
      subtitle: `Confidence ${confidence}% · ${c24 >= 0 ? '+' : ''}${c24?.toFixed(1)}% heute`,
    };
  }

  if (signalType === 'Halten') {
    const topReason = reasons?.[0] || 'Keine klare Richtung';
    return {
      title: `⏸ Watchlist: ${ticker} – Signal: Halten`,
      body: `${name} bei ${priceStr}\n${topReason}`,
      subtitle: `${c24 >= 0 ? '+' : ''}${c24?.toFixed(1)}% heute`,
    };
  }

  return null;
}

async function fireNotification(asset, signalType) {
  const content = buildNotificationContent(asset, signalType);
  if (!content) return;

  if (isNative()) {
    await LocalNotifications.schedule({
      notifications: [{
        id: ++notifIdCounter,
        title: content.title,
        body: content.body,
        extra: { ticker: asset.ticker, signal: signalType },
        smallIcon: 'ic_stat_notify',
        sound: 'default',
        channelId: 'signals',
      }],
    });
  } else {
    // Browser-Fallback
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(content.title, {
        body: content.body,
        tag: `${asset.ticker}-${signalType}`,
      });
    }
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useSignalAlerts() {
  const prevAssetsRef = useRef([]);

  const initializedRef = useRef(false);

  const checkAlerts = useCallback((assets) => {
    if (!assets?.length) return;
    const alerts      = loadAlerts();
    const lastSignals = loadLastSignals();
    const newSignals  = {};

    // Beim ersten Load nur Signale speichern, nicht feuern (Race Condition Fix)
    if (!initializedRef.current) {
      initializedRef.current = true;
      assets.forEach(a => { newSignals[a.ticker] = a.signal; });
      saveLastSignals(newSignals);
      return;
    }

    assets.forEach(asset => {
      const { ticker, signal } = asset;
      newSignals[ticker] = signal;

      const prev = lastSignals[ticker];
      const pref = alerts[ticker];
      if (!pref || !prev || prev === signal) return;

      // Kaufsignal
      if (pref.buy && signal === 'Kaufen') {
        fireNotification(asset, 'Kaufen');
      }
      // Verkaufsignal
      if (pref.sell && signal === 'Verkaufen') {
        fireNotification(asset, 'Verkaufen');
      }
      // Halten — immer feuern wenn Signal sich ändert
      if (signal === 'Halten') {
        fireNotification(asset, 'Halten');
      }
    });

    saveLastSignals(newSignals);
  }, []);

  const onAssetsUpdate = useCallback((assets) => {
    if (assets !== prevAssetsRef.current) {
      prevAssetsRef.current = assets;
      checkAlerts(assets);
    }
  }, [checkAlerts]);

  const getAlert = useCallback((ticker) => {
    return loadAlerts()[ticker] || { buy: false, sell: false };
  }, []);

  const setAlert = useCallback(async (ticker, type, enabled) => {
    if (enabled) {
      const granted = await requestPermission();
      if (!granted) return false;
    }
    const alerts = loadAlerts();
    alerts[ticker] = { ...(alerts[ticker] || { buy: false, sell: false }), [type]: enabled };
    saveAlerts(alerts);
    return true;
  }, []);

  const removeAlerts = useCallback((ticker) => {
    const alerts = loadAlerts();
    delete alerts[ticker];
    saveAlerts(alerts);
  }, []);

  return { getAlert, setAlert, removeAlerts, onAssetsUpdate };
}
